import * as React from 'react';
import {defaults, constant} from 'lodash';
import {TransitionMotion, spring} from 'react-motion';
import Balloon from './Balloon';
import {translate} from '@packages/utilities/inline-style-utils';
import {omit} from '@packages/utilities/fp/utils';
import {place, positions, centerOffset, pad} from '@packages/utilities/box';
import {snappy} from './constants/Springs';
import {
  designerTheme as theme,
  styledDiv,
  type Theme,
  emotionStyled,
} from '@packages/utilities/styled';
import type {Box} from '@packages/utilities/box';
import type {CSSProperties} from '@packages/utilities/css-properties-type';
import useResizeObserver from '@packages/systems/ui/hooks/useResizeObserver';
import {useRegisterLegacyOverlay} from '@packages/systems/spring';
import {FloatingNode} from '@floating-ui/react';

const keepBodyStyles = omit([
  'backgroundColor',
  'position',
  'transform',
  'top',
  'left',
]);

const borderRadius = 2;
const arrowSizeDefault = 6;
const gap = 2;
const padAnchor = pad(arrowSizeDefault + gap);
const padViewport = pad(-8);

export type PlacementType = keyof typeof translations;

const Wrapper = emotionStyled('div')({
  position: 'absolute',
  pointerEvents: 'none',
});

const Body = styledDiv(
  ({
    theme: {gutters, fontSizes, control, lineHeights},
    style,
  }: {
    theme: Theme;
    style?: CSSProperties;
  }) => ({
    boxSizing: 'border-box',
    position: 'relative',
    maxWidth: (style && style.maxWidth) || 254,
    paddingTop: 0,
    paddingBottom: 0,
    padding: gutters.narrow,
    fontSize: fontSizes.copy,
    color: control.textColor,
    lineHeight: `${lineHeights.copy}px`,
    ...style,
  })
);

type TooltipBaseSharedProps = {
  placement: PlacementType;
  dropShadow?: boolean;
  dropShadowLight?: boolean;
  showBorder?: boolean;
  anchor?: Box;
  viewport: Box;
  children: React.ReactNode;
  arrowSize?: number;
  alternate?: boolean;
  targetRef?: React.RefObject<HTMLDivElement>;
};

type TooltipBaseContentProps = {
  children: React.ReactNode;
  style: CSSProperties;
};

type SharedStyles = CSSProperties & Box;

type OuterStyles = {
  backgroundColor: string;
};

type InnerStyles = OuterStyles & {
  transform: string;
};

type TooltipBaseInnerProps = TooltipBaseSharedProps & {
  style: SharedStyles & InnerStyles;
};

type TooltipBaseOuterProps = TooltipBaseSharedProps & {
  style: SharedStyles & OuterStyles;
};

const TooltipBaseContent = ({children, style}: TooltipBaseContentProps) => {
  const elRef = React.useRef(null);
  const [size, setSize] = React.useState({
    width: style.width || 0,
    height: style.height || 0,
  });

  React.useEffect(() => {
    // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type '{}'. | TS2322 - Type 'unknown' is not assignable to type '{}'.
    setSize({width: style.width, height: style.height});
  }, [style.width, style.height]);

  const onResize = (entries: Array<ResizeObserverEntry>) => {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    const {width, height} = entries[0].contentRect;
    setSize({width, height});
  };

  const resizeObserver = useResizeObserver(onResize);

  // @ts-expect-error - TS2344 - Type 'string' does not satisfy the constraint 'keyof IntrinsicElements | ForwardRefExoticComponent<any> | (new (props: any) => Component<any, {}, any>) | ((props: any, context?: any) => ReactElement<...> | null)'.
  const setRef = (el: null | HTMLDivElement | React.ElementRef<string>) => {
    if (elRef.current == null && el) {
      resizeObserver.observe(el);
      // @ts-expect-error - TS2322 - Type 'HTMLDivElement' is not assignable to type 'null'.
      elRef.current = el;
    }
  };

  const bodyStyles = {
    ...style,
    height: null,
    width: null,
    minWidth: size.width,
    minHeight: size.height,
  } as const;

  return (
    // @ts-expect-error - TS2322 - Type '{ children: Element; style: { readonly height: null; readonly width: null; readonly minWidth: {}; readonly minHeight: {}; readonly accentColor?: unknown; readonly alignContent?: unknown; readonly alignItems?: unknown; ... 778 more ...; readonly "-webkit-user-select"?: unknown; }; }' is not assignable to type 'IntrinsicAttributes & Diff<{ theme: Theme; style?: ObjMap<StrictCSSProperties, ToMaybeType> | undefined; }, { ...; }> & RefAttributes<...> & { ...; }'.
    <Body style={bodyStyles}>
      <div ref={setRef}>{children}</div>
    </Body>
  );
};

const TooltipBase = ({
  alternate,
  dropShadow = false,
  dropShadowLight = false,
  showBorder,
  placement,
  viewport,
  anchor = {top: 0, left: 0, height: 0, width: 0},
  // @ts-expect-error - TS2322 - Type '{}' is not assignable to type 'ObjMap<StrictCSSProperties, ToMaybeType> & Box & OuterStyles & { transform: string; }'.
  style = {},
  children,
  arrowSize = arrowSizeDefault,
  targetRef,
  ...rest
}: TooltipBaseInnerProps) => {
  const paddedViewport = padViewport(viewport);

  const baseContentStyles = {
    ...(keepBodyStyles(style) as any),
    maxWidth: paddedViewport.width,
  } as const;

  if (!style.width) {
    return (
      <Wrapper ref={targetRef} {...rest}>
        <Body style={baseContentStyles}>{children}</Body>
      </Wrapper>
    );
  }

  const paddedAnchor = padAnchor(anchor);
  const popoverPlacement = place(placement)(
    paddedViewport,
    paddedAnchor,
    style
  );
  const popoverRect = positions[popoverPlacement](
    paddedViewport,
    paddedAnchor,
    style
  );

  return (
    <Wrapper
      ref={targetRef}
      style={{
        top: popoverRect.top,
        left: popoverRect.left,
        transform: style.transform,
      }}
      {...rest}
    >
      <Balloon
        alternate={alternate}
        style={{
          // @ts-expect-error - TS2322 - Type '{ position: string; width: number; height: number; borderRadius: number; color: string; borderColor: {}; }' is not assignable to type 'StyleObject'.
          position: 'absolute',
          width: popoverRect.width,
          height: popoverRect.height,
          borderRadius,
          color: style.backgroundColor,
          borderColor: style.borderColor || theme.colors.borderDeep,
        }}
        dropShadow={dropShadow}
        dropShadowLight={dropShadowLight}
        showBorder={showBorder}
        position={popoverPlacement}
        arrowSize={arrowSize}
        arrowOffset={centerOffset[popoverPlacement](anchor, popoverRect)}
      />
      <TooltipBaseContent style={baseContentStyles}>
        {children}
      </TooltipBaseContent>
    </Wrapper>
  );
};

const willLeave = constant({v: spring(0, snappy)});
const defaultStyles = [{key: 'a', style: {v: 0}}];
const styles = [{key: 'a', style: {v: spring(10, snappy)}}];

const translations = {
  // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
  top: (value) => translate(0, value, 0),
  // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
  bottom: (value) => translate(0, -value, 0),
  // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
  left: (value) => translate(value, 0, 0),
  // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
  right: (value) => translate(-value, 0, 0),
  // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type.
  center: (value) => translate(0, -value, 0),
} as const;

/**
 * @deprecated use `Tooltip` from `@packages/systems/spring` instead
 */
export default (props: TooltipBaseOuterProps) => {
  const {nodeId} = useRegisterLegacyOverlay({isOpen: true});

  return (
    <FloatingNode id={nodeId}>
      <TransitionMotion
        willLeave={willLeave}
        defaultStyles={defaultStyles}
        styles={styles}
      >
        {/* @ts-expect-error - TS2769 - No overload matches this call. */}
        {([
          {
            style: {v},
            key,
          },
        ]: [any]) =>
          TooltipBase(
            defaults(
              {
                key,
                style: defaults(
                  {
                    opacity: v / 10,
                    transform: translations[props.placement || 'top'](10 - v),
                  },
                  props.style || {}
                ),
              },
              props
            )
          )
        }
      </TransitionMotion>
    </FloatingNode>
  );
};
